import React, { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, Grid, IconButton } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { startOfDay, isBefore, addDays } from 'date-fns';
import { Reservation, getErrorUnknown } from '@lib/state';
import { useBulkBookingValidationQuery } from './useBookingValidationQuery';
import { BookingValidationDisplay } from './booking-validation-display';
import { ErrorDisplay, SubmitButton } from '../../forms';
import { LoadingIndicator } from '../../components';
import CancelIcon from '@material-ui/icons/Cancel';

interface Props {
  reservations: Array<Reservation>;
  corporateAccountId: string;
  propertyId: string;
  error?: Error;
}

const BulkChangeDatesReservationsFormComponent: React.FC<Props> = ({
  reservations,
  corporateAccountId,
  propertyId,
  error,
}) => {
  const { register, getValues, watch, setValue, errors } = useFormContext();
  const checkInDate = watch('checkInDate', null);
  const checkOutDate = watch('checkOutDate', null);

  useEffect(() => {
    register(
      { name: 'checkInDate' },
      {
        validate: value => {
          const checkOutValue = getValues('checkOutDate');
          return !value || !checkOutValue || isBefore(value, checkOutValue);
        },
      }
    );

    register(
      { name: 'checkOutDate' },
      {
        validate: value => {
          const checkInValue = getValues('checkInDate');
          return !value || !checkInValue || isBefore(checkInValue, value);
        },
      }
    );

    setValue('checkInDate', null);
    setValue('checkOutDate', null);
  }, []);

  const queryResult = useBulkBookingValidationQuery({
    corporateAccountId: corporateAccountId,
    propertyId: propertyId,
    reservationIds: reservations.map(r => r.id),
    checkInDate: checkInDate,
    checkOutDate: checkOutDate,
  });

  const validation = queryResult.data;

  const allErrors: Error | undefined = useMemo(() => {
    if (error) return error;
    if (queryResult.isError) return getErrorUnknown(queryResult.error);
    return undefined;
  }, [error, queryResult.isError]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DatePicker
            autoOk
            disableToolbar
            disablePast
            // PopoverProps allows us to pass properties directly to the underlying Popover component
            PopoverProps={{
              // Setting disablePortal to true changes the rendering container to 'inline'
              // This means that instead of rendering the DatePicker at the end of the body element (the default behavior),
              // it will be rendered directly within its parent DOM node.
              // This can help solve layering issues because the DatePicker is layered directly on top of its parent node in the DOM.
              disablePortal: true,
            }}
            fullWidth
            variant="inline"
            format="MMMM d"
            label="Check-In Date"
            name="checkInDate"
            maxDate={checkOutDate ? startOfDay(addDays(checkOutDate, -1)) : undefined}
            value={checkInDate}
            onChange={value => setValue('checkInDate', value && startOfDay(value), true)}
            color="primary"
            size="small"
            InputProps={{
              endAdornment: (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton onClick={() => setValue('checkInDate', null)}>
                    <CancelIcon />
                  </IconButton>
                  <FontAwesomeIcon icon="calendar-week" />
                </div>
              ),
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            autoOk
            disableToolbar
            disablePast
            // PopoverProps allows us to pass properties directly to the underlying Popover component
            PopoverProps={{
              // Setting disablePortal to true changes the rendering container to 'inline'
              // This means that instead of rendering the DatePicker at the end of the body element (the default behavior),
              // it will be rendered directly within its parent DOM node.
              // This can help solve layering issues because the DatePicker is layered directly on top of its parent node in the DOM.
              disablePortal: true,
            }}
            fullWidth
            variant="inline"
            format="MMMM d"
            label="Check-Out Date"
            name="checkOutDate"
            value={checkOutDate}
            error={!!errors.checkOutDate}
            onChange={value => setValue('checkOutDate', value && startOfDay(value), true)}
            color="primary"
            size="small"
            InputProps={{
              endAdornment: (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton onClick={() => setValue('checkOutDate', null)}>
                    <CancelIcon />
                  </IconButton>
                  <FontAwesomeIcon icon="calendar-week" />
                </div>
              ),
            }}
          />
        </Grid>
      </Grid>
      {queryResult.isLoading && <LoadingIndicator loadingText="Please wait..." />}
      {!!validation && <BookingValidationDisplay validation={validation} />}
      <Box my={3}>
        <ErrorDisplay error={allErrors} />
        <SubmitButton
          variant="contained"
          color="secondary"
          fullWidth
          disabled={!validation?.isValid}
        >
          Check Availability
        </SubmitButton>
      </Box>
    </>
  );
};

export const BulkChangeDatesReservationsForm = BulkChangeDatesReservationsFormComponent;
